import { defineStore } from 'pinia';
import { Store } from '@/setup/globals';
import { setInitialData } from '@/stores/initial-data';
import useSessionStore from '@/stores/session';
import getApiUrl from '@/helpers/get-api-url';
import { Pagination } from '@/helpers/pagination';
import { ApiProductList } from '@/types/api-response';
import { Applications, Product, ProductBase, SetsAndKits } from '@/types/product';

export type StoreState = {
  product: Product | null;
  filterResultFavoritesIds: number[];

  /**
   * Holds the currently running requests.
   */
  runningRequests: {
    apiFetchProductVariants: boolean;
    apiFetchProductBundles: boolean;
    apiFetchProductList: boolean;
    apiFetchProductSetsAndKits: boolean;
  };
};

const storeName = Store.Product;

export default defineStore(storeName, {
  state: () => {
    const state: StoreState = {
      product: null,
      filterResultFavoritesIds: [],

      runningRequests: {
        apiFetchProductVariants: false,
        apiFetchProductBundles: false,
        apiFetchProductList: false,
        apiFetchProductSetsAndKits: false,
      },
    };

    const initialData: StoreState = window.initialData?.[storeName] || {};

    setInitialData(state, initialData);

    return state;
  },
  actions: {
    async apiFetchProductVariants(productId: number): Promise<ProductBase[]> {
      this.runningRequests.apiFetchProductVariants = true;

      const sessionStore = useSessionStore();

      const url = getApiUrl('getProductVariants', {
        id: String(productId),
        locale: sessionStore.locale,
      });

      try {
        const response = await this.$api.get(url);

        if ('data' in response) {
          return response.data.data;
        }

        throw new Error('API failure during "product/apiFetchProductVariants" request.');
      } catch (error) {
        return Promise.reject(error); // Explicitly reject with the caught error
      } finally {
        this.runningRequests.apiFetchProductVariants = false;
      }
    },

    // eslint-disable-next-line max-params
    async apiFetchProductBundles(productId: number, pagination: Pagination): Promise<Applications> {
      this.runningRequests.apiFetchProductBundles = true;

      const sessionStore = useSessionStore();
      const url = getApiUrl('getProductBundles', {
        id: String(productId),
        locale: sessionStore.locale,
        page: String(pagination.current),
        limit: String(pagination.numItemsPerPage),
      });

      try {
        const response = await this.$api.get(url);

        if ('data' in response) {
          return response.data;
        }

        throw new Error('API failure during "product/apiFetchProductBundles" request.');
      } catch (error) {
        return Promise.reject(error); // Explicitly reject with the caught error
      } finally {
        this.runningRequests.apiFetchProductBundles = false;
      }
    },

    // eslint-disable-next-line max-params
    async apiFetchProductSetsAndKits(productId: number, pagination: Pagination): Promise<SetsAndKits> {
      this.runningRequests.apiFetchProductSetsAndKits = true;

      const sessionStore = useSessionStore();
      const url = getApiUrl('getProductSetsAndKits', {
        id: String(productId),
        locale: sessionStore.locale,
        page: String(pagination.current),
        limit: String(pagination.numItemsPerPage),
      });

      try {
        const response = await this.$api.get(url);

        if ('data' in response) {
          return response.data;
        }

        throw new Error('API failure during "product/apiFetchProductSetsAndKits" request.');
      } catch (error) {
        return Promise.reject(error); // Explicitly reject with the caught error
      } finally {
        this.runningRequests.apiFetchProductSetsAndKits = false;
      }
    },

    // eslint-disable-next-line max-params
    async apiFetchProductList(filter: string, categoryId: number, pagination: Pagination): Promise<ApiProductList> {
      this.runningRequests.apiFetchProductList = true;

      const sessionStore = useSessionStore();
      const url = getApiUrl('getProductList', {
        category: String(categoryId),
        locale: sessionStore.locale,
        page: String(pagination.current),
        limit: String(pagination.numItemsPerPage),
        filter,
      });

      try {
        const response = await this.$api.get(url);

        response.data.pagination.current = response.data.pagination?.current || 1;

        if ('filterResultFavoritesIds' in response.data && Array.isArray(response.data.filterResultFavoritesIds)) {
          this.filterResultFavoritesIds = response.data.filterResultFavoritesIds;
        }

        if ('data' in response) {
          return response.data;
        }

        throw new Error('API failure during "product/apiFetchProductList" request.');
      } catch (error) {
        return Promise.reject(error); // Explicitly reject with the caught error
      } finally {
        this.runningRequests.apiFetchProductList = false;
      }
    },
  },
});
